import {css} from 'aphrodite';
import classNames from 'classnames';
import React, {Dispatch, SetStateAction} from 'react';

import BLASTQueryChips from 'search/blast/BLASTQueryChips';
import {BLASTInputSource} from 'search/blast/BLASTTracking';
import {
  BLASTN_TYPES,
  BLASTP_TYPES,
  hasNucleotideInput,
  makeSequenceLabel,
  stripWhitespace,
} from 'search/blast/BLASTUtil';
import {styles} from 'search/blast/constants';
import * as FileExtractionActions from 'search/blast/FileExtractionActions';
import {
  BLASTSequenceValidationState,
  MAX_BLAST_NUCL_ONLY_AA,
  MAX_BLAST_NUCLEOTIDES,
  validateBLASTSequence,
} from 'search/blast/validation';
import SearchInput from 'search/inputs/SearchInput';
import {BLASTFilter, BLASTOperator, BLASTTextSequence} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FileChooser from 'ui-library/FileChooser';
import Spinner from 'ui-library/Spinner';
import {TextAreaChangeEvent} from 'util/Types';

const NUCLEOTIDE_DISPLAY_LABEL = 'nucleotides';
const AMINO_ACIDS_DISPLAY_LABEL = 'amino acids';

function sequenceValidationMessage(
  validationState: BLASTSequenceValidationState,
  operator: BLASTOperator
): string {
  if (validationState.isValid) {
    return '';
  }

  if (validationState.isLongNucleotideOnlyAA) {
    return `Amino acid sequences longer than ${MAX_BLAST_NUCL_ONLY_AA} residues cannot consist entirely of nucleotides.`;
  }

  let displayResidueType: string;
  let displayMaxLength: string;

  if (hasNucleotideInput(operator)) {
    displayResidueType = NUCLEOTIDE_DISPLAY_LABEL;
    displayMaxLength = `${(MAX_BLAST_NUCLEOTIDES / 1000).toString()} kb`;
  } else {
    displayResidueType = AMINO_ACIDS_DISPLAY_LABEL;
    displayMaxLength = '1 million residues';
  }

  const lengthMessage = `exceeds our max length of ${displayMaxLength}`;
  const residuesMessage = `contains invalid ${displayResidueType}`;
  let errors: string;

  if (!validationState.isValidLength && !validationState.hasValidResidues) {
    errors = `${residuesMessage} and also ${lengthMessage}`;
  } else if (!validationState.isValidLength) {
    errors = lengthMessage;
  } else {
    errors = residuesMessage;
  }

  return `Your query ${errors}.`;
}

export interface SelectSequencesPaneProps {
  filter: BLASTFilter;
  setFilter: Dispatch<SetStateAction<BLASTFilter>>;
  setInputSources: Dispatch<SetStateAction<Record<string, BLASTInputSource>>>;
  sequence: string;
  setSequence: Dispatch<SetStateAction<string>>;
  fileExtractionsInProgress: number;
  setFileExtractionsInProgress: Dispatch<SetStateAction<number>>;
}

export default function SelectSequencesPane({
  filter,
  setFilter,
  setInputSources,
  sequence,
  setSequence,
  fileExtractionsInProgress,
  setFileExtractionsInProgress,
}: SelectSequencesPaneProps): JSX.Element {
  const sequenceWithoutWhitespace = stripWhitespace(sequence);

  const onChangeSequence = (e: TextAreaChangeEvent): void => {
    const inputText = e.target.value;
    setSequence(inputText);
  };

  const onAddSequence = (): void => {
    if (sequenceWithoutWhitespace) {
      const newSequenceLabel = makeSequenceLabel(sequenceWithoutWhitespace, filter.value.textSequences);
      setFilter((previousFilter) => ({
        ...previousFilter,
        value: {
          ...previousFilter.value,
          textSequences: [
            ...previousFilter.value.textSequences,
            {
              name: newSequenceLabel,
              sequence: sequenceWithoutWhitespace,
            },
          ],
        },
      }));
      setSequence('');
      setInputSources((prevSources) => ({
        ...prevSources,
        [newSequenceLabel]: BLASTInputSource.TextInput,
      }));
    }
  };

  let sequenceLabel: string;
  let residueType: string;
  let uploadLabel: string;
  let entitySearchTypes: string;

  if (hasNucleotideInput(filter.operator as BLASTOperator)) {
    sequenceLabel = 'Enter a nucleotide sequence.';
    residueType = NUCLEOTIDE_DISPLAY_LABEL;
    uploadLabel = 'Upload a sequence or trace file (.ab1, .ftv, .fasta, .gb, .geneious).';
    entitySearchTypes = 'DNA sequence or oligo';
  } else {
    sequenceLabel = 'Enter an amino acid sequence.';
    residueType = AMINO_ACIDS_DISPLAY_LABEL;
    uploadLabel = 'Upload a sequence file (.fasta, .faa).';
    entitySearchTypes = 'AA sequence';
  }

  const textInputValidationState = validateBLASTSequence(
    sequenceWithoutWhitespace,
    filter.operator as BLASTOperator
  );

  return (
    <>
      <div>
        <FormGroup>
          <Label className={classNames('u-moderate-spacing-bottom', css(styles.sectionLabel))}>
            {sequenceLabel}
          </Label>
          <div className={classNames('u-flex-display', 'u-small-spacing-bottom', css(styles.sequenceInput))}>
            <TextArea
              placeholder={`Type or paste in ${residueType}.`}
              value={sequence}
              onChange={onChangeSequence}
              rows={3}
              autoFocus={true}
              className={css(!textInputValidationState.isValid && styles.errorInput)}
            />
            <Button
              disabled={!sequenceWithoutWhitespace.length || !textInputValidationState.isValid}
              className="u-mini-spacing-left"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={onAddSequence}
            >
              Add
            </Button>
          </div>
          {!textInputValidationState.isValid && (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={sequenceValidationMessage(textInputValidationState, filter.operator as BLASTOperator)}
            />
          )}
          {textInputValidationState.isValid && textInputValidationState.isNucleotideOnlyAA && (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="This looks like a nucleotide sequence. Did you enter it by mistake?"
            />
          )}
        </FormGroup>
        <hr />
        <FormGroup>
          <Label className={classNames('u-moderate-spacing-bottom', css(styles.sectionLabel))}>
            {uploadLabel}
            {fileExtractionsInProgress > 0 && <Spinner classNames={css(styles.fileSpinner)} />}
          </Label>
          <FileChooser
            onSelectFiles={async (...newFiles: Array<File>) => {
              setFileExtractionsInProgress((previousValue) => previousValue + 1);
              try {
                const extractedSequences = await FileExtractionActions.extractBLASTQuerySequencesFromFiles(
                  filter.operator as BLASTOperator,
                  newFiles
                );

                /*
                 * Need to make a copy and keep the copy updated with the new sequences.
                 * This is because makeSequenceLabel requires its second parameter to be
                 * up-to-date to generate the correct new name. Otherwise, if multiple
                 * extracted sequences are the same, they will have the same name.
                 */
                const textSequencesCopy: Array<BLASTTextSequence> = [...filter.value.textSequences];
                const newSequenceSources: Record<string, BLASTInputSource> = {};
                for (const extractedSequence of extractedSequences) {
                  const newSequenceName = makeSequenceLabel(extractedSequence.sequence, textSequencesCopy);
                  textSequencesCopy.push({
                    name: extractedSequence.name || newSequenceName,
                    sequence: extractedSequence.sequence,
                  });
                  newSequenceSources[newSequenceName] = BLASTInputSource.FileUpload;
                }

                setFilter((previousFilter) => ({
                  ...previousFilter,
                  value: {
                    ...previousFilter.value,
                    textSequences: textSequencesCopy,
                  },
                }));
                setInputSources((prevSources) => ({
                  ...prevSources,
                  ...newSequenceSources,
                }));
              } finally {
                setFileExtractionsInProgress((previousValue) => previousValue - 1);
              }
            }}
            multiple={false}
          />
        </FormGroup>
        <hr />
        <FormGroup>
          <Label className={classNames('u-moderate-spacing-bottom', css(styles.sectionLabel))}>
            BLAST a Benchling {entitySearchTypes}.
          </Label>
          <SearchInput
            className={css(styles.entitySearch)}
            isMulti={true}
            hideChips={true}
            autoFocus={false}
            preserveQueryOnSelect={true}
            placeholder="Search by name"
            selected={filter.value.apiIdentifiers}
            onSelect={(id) => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                value: {
                  ...previousFilter.value,
                  apiIdentifiers: [...previousFilter.value.apiIdentifiers, id!],
                },
              }));
              setInputSources((prevSources) => ({
                ...prevSources,
                [id!]: BLASTInputSource.BenchlingEntity,
              }));
            }}
            onRemove={(id) => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                value: {
                  ...previousFilter.value,
                  apiIdentifiers: previousFilter.value.apiIdentifiers.filter(
                    (previousId) => previousId !== id
                  ),
                },
              }));
              setInputSources((prevSources) => {
                const newSources = {...prevSources};
                delete newSources[id!];
                return newSources;
              });
            }}
            types={hasNucleotideInput(filter.operator as BLASTOperator) ? BLASTN_TYPES : BLASTP_TYPES}
          />
        </FormGroup>
        <hr />
        <BLASTQueryChips
          filter={filter}
          onRemoveSequence={(sequenceToRemove: string, type: 'sequence' | 'entity') => {
            if (type === 'sequence') {
              setFilter((prevFilter) => ({
                ...prevFilter,
                value: {
                  ...prevFilter.value,
                  textSequences: prevFilter.value.textSequences.filter((ts) => ts.name !== sequenceToRemove),
                },
              }));
            } else {
              setFilter((prevFilter) => ({
                ...prevFilter,
                value: {
                  ...prevFilter.value,
                  apiIdentifiers: prevFilter.value.apiIdentifiers.filter((id) => id !== sequenceToRemove),
                },
              }));
            }
            setInputSources((prevSources) => {
              const newSources = {...prevSources};
              delete newSources[sequenceToRemove];
              return newSources;
            });
          }}
        />
      </div>
    </>
  );
}
